import type { FileUploadProps } from "@wayflyer/flyui";
import { Button, Flex } from "@wayflyer/flyui";
import { IconPlus16Line } from "@wayflyer/flyui-icons/16/line";
import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { styled } from "styled-components";

import type { KYCDocumentUploadType } from "../../api-types/associated_persons_schemas";
import { useKycForms } from "../../contexts";
import type { Configuration } from "../interface";
import type { PersonSummary } from "../person-card";
import { PersonCard } from "../person-card";

export interface VerifyPersonsFormProps<T> {
  onAddPersonClick: EventHandler;
  onPersonConfigurationChangeHandler: (id: number, value: Record<string, boolean>) => void;
  onStartVerification: (id: number) => void;
  onOpenPersonForm: (id?: number) => void;
  onRemovePerson: (id: number) => void;
  kycConfiguration: Configuration;
  usePersonUploadManager: ({
    id,
    documentType,
  }: {
    id?: number;
    documentType?: KYCDocumentUploadType;
  }) => FileUploadProps<T>;
  onReRequestIdentityPerson: (id: number) => void;
}

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const AddButton = styled(Button)`
  min-width: unset;
  min-height: 40px;
  @media (max-width: 640px) {
    height: auto;
  }
`;

/**
 * VerifyPersonsForm component
 */
export const VerifyPersonsForm = <T,>({
  onAddPersonClick,
  onPersonConfigurationChangeHandler,
  onStartVerification,
  onOpenPersonForm,
  onRemovePerson,
  onReRequestIdentityPerson,
  kycConfiguration,
  usePersonUploadManager,
}: VerifyPersonsFormProps<T>) => {
  const { kycPersonFields, updateKycPerson } = useKycForms();
  const intl = useIntl();

  // TODO Clear after contract signer step for ALL jurisdiction will be done
  const handleConfigurationChange = useCallback(
    (id) => (value) => {
      updateKycPerson(id, value);
      onPersonConfigurationChangeHandler(id, value);
    },
    [onPersonConfigurationChangeHandler, updateKycPerson]
  );

  const signeeCount = useMemo(() => kycPersonFields.filter(({ isSignee }) => isSignee).length, [kycPersonFields]);
  return (
    <Flex direction="column" gap="4">
      {kycPersonFields.map((person) => (
        <PersonCard
          {...(person as PersonSummary)}
          onSigneeChange={handleConfigurationChange(person.serverId)}
          onStartVerification={onStartVerification}
          onOpenPersonForm={onOpenPersonForm}
          onRemovePerson={onRemovePerson}
          onReRequestIdentityPerson={onReRequestIdentityPerson}
          kycConfiguration={kycConfiguration}
          signeeCount={signeeCount}
          usePersonUploadManager={usePersonUploadManager}
          key={person.serverId}
        />
      ))}
      <ButtonContainer>
        <AddButton iconLeft={IconPlus16Line} variant="Secondary" size="normal" onClick={onAddPersonClick}>
          {intl.formatMessage({
            id: "Add an executive, owner or contract signer",
            defaultMessage: "Add an executive, owner or contract signer",
          })}
        </AddButton>
      </ButtonContainer>
    </Flex>
  );
};
